import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { employeeAPI } from '../../lib/api';
import { ScrollArea } from '../ui/scroll-area';
import logo from '../../assets/teamly-light.png';
import logoSmall from '../../assets/teamly-small.svg';
import GlobalSearch from './GlobalSearch';
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  DollarSign,
  Receipt,
  Target,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Calendar,
  Banknote,
  UserPlus,
  ShieldCheck,
  FileText,
  Newspaper,
  Clock,
  Monitor,
  Briefcase,
  CreditCard,
  Network,
  Printer,
  ClipboardList,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';

const CompanySidebar = ({ mobile, onMobileClose, company, collapsed, setCollapsed }) => {
  const { user, login, logout, isAdminOrHR, isEmployee, activeRole, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const [isManager, setIsManager] = useState(false);

  useEffect(() => {
    if (isEmployee()) {
      employeeAPI.isManager().then(res => setIsManager(res.data.is_manager)).catch(() => { });
    }
  }, [user?.employee_id]);

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('http')) return logoPath;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
    return `${backendUrl}${logoPath}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleMenu = (menuId) => {
    setOpenMenus(prev => (prev[menuId] ? {} : { [menuId]: true }));
  };

  // Auto-expand menu containing active route and close others
  useEffect(() => {
    const menuGroups = getNavStructure();
    let activeGroupId = null;
    menuGroups.forEach(group => {
      if (group.children) {
        const isActive = group.children.some(item => location.pathname === item.href);
        if (isActive) {
          activeGroupId = group.id;
        }
      }
    });

    if (activeGroupId) {
      setOpenMenus({ [activeGroupId]: true });
    } else {
      setOpenMenus({});
    }
  }, [location.pathname]);

  const getNavStructure = () => {
    if (isAdminOrHR()) {
      return [
        {
          category: 'Overview',
          items: [
            { type: 'item', title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, module: null },
            { type: 'item', title: 'Feeds', href: '/feeds', icon: Newspaper, module: 'feeds' },
          ]
        },
        {
          category: 'Core HR',
          items: [
            { type: 'item', title: 'Employees', href: '/employees', icon: Users, module: 'employees' },
            { type: 'item', title: 'Onboarding', href: '/onboarding', icon: UserPlus, module: 'onboarding' },
            { type: 'item', title: 'Leave Management', href: '/leave', icon: CalendarDays, module: 'leave' },
            { type: 'item', title: 'Holidays', href: '/holidays', icon: Calendar, module: 'leave' },
            { type: 'item', title: 'Attendance', href: '/attendance', icon: Clock, module: 'attendance' },
            { type: 'item', title: 'Performance', href: '/performance', icon: Target, module: 'performance' },
          ]
        },
        {
          category: 'Finance & Payroll',
          items: [
            { type: 'item', title: 'Salary Structure', href: '/salary-structure', icon: DollarSign, module: 'salary_structure' },
            { type: 'item', title: 'Salary Advance', href: '/salary-advances', icon: Banknote, module: 'salary_advances' },
            { type: 'item', title: 'Payroll Run', href: '/payroll', icon: Receipt, module: 'payroll' },
            { type: 'item', title: 'Tax Management', href: '/tax-management', icon: ShieldCheck, module: 'tax' },
            { type: 'item', title: 'Full & Final', href: '/fnf', icon: Banknote, module: 'payroll' },
          ]
        },
        {
          category: 'Company & Office',
          items: [
            { type: 'item', title: 'IT Assets', href: '/assets', icon: Monitor, module: 'assets' },
            { type: 'item', title: 'Org Chart', href: '/org-chart', icon: Network, module: 'org_chart' },
            { type: 'item', title: 'Resignation Management', href: '/resignation', icon: LogOut, module: 'employees' },
            { type: 'item', title: 'Stationery', href: '/stationery', icon: Printer, module: 'stationery' },
            { type: 'item', title: 'Company Policies', href: '/policies', icon: FileText, module: null },
          ]
        },
        {
          category: 'Administration',
          items: [
            { type: 'item', title: 'Letters & Reports', href: '/letters-reports', icon: FileText, module: 'letters_reports' },
            { type: 'item', title: 'Users & Roles', href: '/users-roles', icon: ShieldCheck, module: null },
            { type: 'item', title: 'Audit Logs', href: '/audit-logs', icon: ClipboardList, module: null },
            { type: 'item', title: 'Settings', href: '/settings', icon: Settings, module: null },
          ]
        }
      ];
    } else {
      // Employee self-service items
      return [
        {
          category: 'My Space',
          items: [
            { type: 'item', title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, module: null },
            { type: 'item', title: 'Feeds', href: '/feeds', icon: Newspaper, module: 'feeds' },
            { type: 'item', title: 'My Profile', href: '/my-profile', icon: Users, module: 'employees' },
            { type: 'item', title: 'Employee Directory', href: '/directory', icon: Users, module: 'employees' },
          ]
        },
        {
          category: 'Time & Attendance',
          items: [
            { type: 'item', title: 'My Leaves', href: '/my-leaves', icon: CalendarDays, module: 'leave' },
            ...(isManager ? [{ type: 'item', title: 'Team Leaves', href: '/team-leaves', icon: Users, module: 'leave' }] : []),
            ...(isManager ? [{ type: 'item', title: 'Team Attendance', href: '/team-attendance', icon: Clock, module: 'attendance' }] : []),
            { type: 'item', title: 'Holidays', href: '/my-holidays', icon: Calendar, module: 'leave' },
            { type: 'item', title: 'Attendance', href: '/my-attendance', icon: Clock, module: 'attendance' },
          ]
        },
        {
          category: 'Finance & Career',
          items: [
            { type: 'item', title: 'My Payslips', href: '/my-payslips', icon: Receipt, module: 'payroll' },
            { type: 'item', title: 'My Tax', href: '/my-tax', icon: ShieldCheck, module: 'tax' },
            { type: 'item', title: 'Tax Certificates', href: '/tax-certificates', icon: FileText, module: 'tax' },
            { type: 'item', title: 'My Goals', href: '/my-goals', icon: Target, module: 'performance' },
          ]
        },
        {
          category: 'Company & Office',
          items: [
            { type: 'item', title: 'Org Chart', href: '/org-chart', icon: Network, module: 'org_chart' },
            { type: 'item', title: 'My Assets', href: '/my-assets', icon: Monitor, module: 'assets' },
            { type: 'item', title: 'My Resignation', href: '/my-resignation', icon: LogOut, module: 'employees' },
            { type: 'item', title: 'Company Policies', href: '/policies', icon: FileText, module: null },
          ]
        }
      ];
    }
  };

  const isModuleEnabled = (module) => {
    return !module || (company?.modules && company.modules[module]);
  };

  const filterNavItems = (categories) => {
    return categories.map(cat => {
      const filteredItems = cat.items.filter(item => isModuleEnabled(item.module));
      return {
        ...cat,
        items: filteredItems
      };
    }).filter(cat => cat.items.length > 0);
  };

  const navItems = filterNavItems(getNavStructure());

  const handleNavClick = () => {
    if (onMobileClose) {
      onMobileClose();
    }
  };

  const isCollapsed = collapsed && !mobile;

  const renderNavItem = (item) => {
    const isActive = location.pathname === item.href;

    const NavItem = (
      <NavLink
        to={item.href}
        onClick={handleNavClick}
        className={cn(
          'flex items-center gap-3 rounded-lg font-medium transition-all duration-200 group relative',
          !isCollapsed && 'px-3 py-2.5 text-sm mx-2',
          isCollapsed && 'h-10 w-10 mx-auto justify-center',
          isActive
            ? 'bg-amber-400 text-black shadow-sm font-bold'
            : 'text-slate-400 hover:bg-white/5 hover:text-white'
        )}
        data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <item.icon className={cn("h-5 w-5 flex-shrink-0 transition-transform duration-200", !isActive && "group-hover:scale-110")} />
        {!isCollapsed && <span>{item.title}</span>}
        {isActive && !isCollapsed && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/20 rounded-r-full" />
        )}
      </NavLink>
    );

    if (isCollapsed) {
      return (
        <Tooltip key={item.href} delayDuration={0}>
          <TooltipTrigger asChild>
            <div className="flex justify-center w-full py-1">
              {NavItem}
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="font-semibold bg-popover text-popover-foreground border border-border shadow-lg">
            {item.title}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <div key={item.href} className="group-hover/sidebar:translate-x-1 transition-transform">{NavItem}</div>;
  };

  const renderNavGroup = (group) => {
    const isOpen = openMenus[group.id] || false;
    const hasActiveChild = group.children.some(item => location.pathname === item.href);

    if (isCollapsed) {
      // When collapsed, show first child or a tooltip with all children
      return (
        <Tooltip key={group.id} delayDuration={0}>
          <TooltipTrigger asChild>
            <div className="flex justify-center w-full py-1">
              <button
                onClick={() => toggleMenu(group.id)}
                className={cn(
                  'flex items-center justify-center h-10 w-10 rounded-lg font-medium transition-all duration-200',
                  hasActiveChild
                    ? 'bg-amber-400 text-black shadow-sm font-bold'
                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                )}
              >
                <group.icon className="h-5 w-5 flex-shrink-0" />
              </button>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="p-0 overflow-hidden bg-[#111] border border-white/10 shadow-2xl min-w-[180px] rounded-xl backdrop-blur-md">
            <div className="py-2">
              <div className="px-3 py-2 bg-white/5 border-b border-white/5 mb-1.5">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.1em]">{group.title}</p>
              </div>
              <div className="px-1">
                {group.children.map(child => {
                  const isChildActive = location.pathname === child.href;
                  return (
                    <NavLink
                      key={child.href}
                      to={child.href}
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-all duration-150 mx-1 mb-0.5',
                          isActive
                            ? 'bg-amber-400/15 text-amber-400 font-bold'
                            : 'text-slate-300 hover:bg-white/5 hover:text-white'
                        )
                      }
                    >
                      <child.icon className={cn("h-4 w-4 opacity-70", isChildActive && "opacity-100")} />
                      {child.title}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Collapsible key={group.id} open={isOpen} onOpenChange={() => toggleMenu(group.id)}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex items-center justify-between w-full px-3 py-2.5 text-sm rounded-lg font-medium transition-all duration-200 mx-2 mb-0.5 group',
              hasActiveChild
                ? 'text-amber-400 bg-amber-400/10'
                : 'text-slate-400 hover:bg-white/5 hover:text-white'
            )}
            style={{ width: 'calc(100% - 16px)' }}
          >
            <div className="flex items-center gap-3">
              <group.icon className={cn("h-5 w-5 flex-shrink-0 transition-colors", hasActiveChild ? "text-amber-400" : "text-slate-400 group-hover:text-white")} />
              <span>{group.title}</span>
            </div>
            <ChevronDown className={cn(
              'h-4 w-4 transition-transform duration-200 opacity-50 group-hover:opacity-100',
              isOpen && 'rotate-180'
            )} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pl-4 mt-1 space-y-0.5 animate-collapsible-down">
          {group.children.map(child => {
            const isChildActive = location.pathname === child.href;
            return (
              <NavLink
                key={child.href}
                to={child.href}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 text-sm rounded-lg font-medium transition-all duration-200 ml-4 mr-2 border-l-2',
                    isActive
                      ? 'border-amber-400 text-amber-400 bg-amber-400/10 font-bold'
                      : 'border-transparent text-slate-400 hover:bg-white/5 hover:text-white hover:border-white/10'
                  )
                }
                data-testid={`nav-${child.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className={cn("h-1.5 w-1.5 rounded-full", isChildActive ? "bg-amber-400" : "bg-slate-600")} />
                <span>{child.title}</span>
              </NavLink>
            );
          })}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <aside
      className={cn(
        'flex flex-col h-full transition-all duration-300 relative',
        mobile ? 'w-full' : (isCollapsed ? 'w-full' : 'w-full'),
        !mobile && 'z-50 h-screen border-r border-white/5 bg-[#0a0a0a] transition-all duration-300 hidden lg:block'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex h-16 items-center justify-between border-b border-white/5 px-4 bg-black/20 backdrop-blur-sm">
          <div className={cn("flex items-center transition-all duration-300", isCollapsed ? "w-0 overflow-hidden opacity-0" : "w-auto opacity-100")}>
            <div className="flex items-center">
              <img
                src={logo}
                alt="Teamly"
                className="h-7 w-auto object-contain"
              />
            </div>
          </div>

          {isCollapsed && (
            <div className="flex items-center justify-center w-full">
              <img
                src={logoSmall}
                alt="Teamly"
                className="h-8 w-8 object-contain"
              />
            </div>
          )}
        </div>

        {/* Desktop Toggle Button - Vertical Pill Tab */}
        {!mobile && (
          <button

            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              "absolute left-full top-3 h-10 w-2 bg-amber-400 text-black hover:bg-amber-500 border-none z-[100] transition-all duration-300",
              "flex items-center justify-center p-0 cursor-pointer outline-none ring-0 rounded-r-lg"
            )}
            style={{
              // Your custom inline styles go here
              marginLeft: '1px',
            }}
            data-testid="sidebar-toggle"
          >
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4 sidebar-scroll">
          {/* Company Branding */}
          {company?.logo && (
            <div className={cn(
              "px-4 py-3 mb-2 flex items-center gap-3 transition-all duration-300",
              isCollapsed ? "justify-center" : "justify-start"
            )}>
              <div className={cn(
                "rounded-lg overflow-hidden border border-white/20 bg-white p-1.5 flex-shrink-0 transition-all shadow-sm",
                isCollapsed ? "h-9 w-9" : "h-11 w-11"
              )}>
                <img
                  src={getLogoUrl(company.logo)}
                  alt={company.name}
                  className="h-full w-full object-contain"
                />
              </div>
              {!isCollapsed && (
                <div className="overflow-hidden">
                  <p className="text-[10px] font-bold text-amber-400/80 uppercase tracking-[0.2em] truncate">
                    Active Company
                  </p>
                  <p className="text-sm font-black text-white truncate leading-tight">
                    {company.name}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Role Switcher for multi-role users */}
          {user?.roles?.length > 1 && (
            <div className={cn(
              "px-4 py-2 mt-2 transition-all duration-300",
              isCollapsed ? "px-2" : "px-4"
            )}>
              <div className={cn(
                "bg-white/[0.03] border border-white/10 rounded-xl p-1 flex",
                isCollapsed ? "flex-col gap-1 items-center" : "flex-row"
              )}>
                {isCollapsed ? (
                  <>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => switchRole('COMPANY_ADMIN')}
                          className={cn(
                            "h-8 w-8 rounded-lg flex items-center justify-center transition-all",
                            activeRole === 'COMPANY_ADMIN' ? "bg-amber-400 text-black shadow-lg" : "text-slate-500 hover:text-white"
                          )}
                        >
                          <ShieldCheck className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right">Admin View</TooltipContent>
                    </Tooltip>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => switchRole('EMPLOYEE')}
                          className={cn(
                            "h-8 w-8 rounded-lg flex items-center justify-center transition-all",
                            activeRole === 'EMPLOYEE' ? "bg-amber-400 text-black shadow-lg" : "text-slate-500 hover:text-white"
                          )}
                        >
                          <Users className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right">Employee View</TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => switchRole('COMPANY_ADMIN')}
                      className={cn(
                        "flex-1 py-1.5 px-3 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-2",
                        activeRole === 'COMPANY_ADMIN' 
                          ? "bg-amber-400 text-black shadow-lg" 
                          : "text-slate-500 hover:text-white"
                      )}
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Admin
                    </button>
                    <button
                      onClick={() => switchRole('EMPLOYEE')}
                      className={cn(
                        "flex-1 py-1.5 px-3 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-2",
                        activeRole === 'EMPLOYEE' 
                          ? "bg-amber-400 text-black shadow-lg" 
                          : "text-slate-500 hover:text-white"
                      )}
                    >
                      <Users className="h-3.5 w-3.5" />
                      Employee
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          <GlobalSearch collapsed={isCollapsed} />
          <nav className="space-y-4 px-2 mt-2">
            {navItems.map((category) => (
              <div key={category.category} className="space-y-1">
                {!isCollapsed && (
                  <p className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-500/85 mb-1 px-3">
                    {category.category}
                  </p>
                )}
                 <div className="space-y-0.5">
                  {category.items.map(item => renderNavItem(item))}
                </div>
              </div>
            ))}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className={cn(
          "border-t border-white/5 p-3 bg-white/[0.02]",
          isCollapsed ? "flex flex-col items-center gap-4" : ""
        )}>
          <div className={cn(
            "flex items-center gap-3 w-full",
            isCollapsed ? "justify-center" : "justify-between"
          )}>
            <div className="flex items-center gap-3 overflow-hidden group/profile">
              <div className="h-10 w-10 rounded-full bg-amber-400/15 flex items-center justify-center border border-amber-400/30 text-amber-400 font-extrabold text-sm shrink-0">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              {!isCollapsed && (
                <div className="overflow-hidden">
                  <p className="text-sm font-semibold text-white truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-slate-400 capitalize truncate">
                    {user?.role?.replace('_', ' ').toLowerCase()}
                  </p>
                </div>
              )}
            </div>

            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={handleLogout}
                  className={cn(
                    "p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition-all shrink-0",
                    isCollapsed ? "h-10 w-10 flex items-center justify-center" : ""
                  )}
                  data-testid="logout-btn"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10} className="font-medium text-red-500 bg-[#0a0a0a] border-white/10">
                Logout
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default CompanySidebar;
